/**
 * Currency Exchange Rate Service
 * 
 * Використовує безкоштовні API для отримання курсів валют:
 * - НБУ (Національний банк України) - основний, безкоштовний, без ключів
 * - Monobank API - резервний, безкоштовний, без ключів
 */

export interface ExchangeRates {
  [currency: string]: number; // курс відносно базової валюти (USD)
}

interface NBURate {
  r030: number; // цифровий код валюти
  txt: string; // повна назва валюти
  rate: number; // курс гривні до валюти (скільки UAH за 1 одиницю валюти)
  cc: string; // літерний код валюти (USD, EUR, тощо)
  exchangedate: string; // дата встановлення курсу
}

interface MonobankRate {
  currencyCodeA: number; // код валюти A (ISO 4217)
  currencyCodeB: number; // код валюти B (ISO 4217, зазвичай 980 = UAH)
  date: number; // дата курсу (Unix timestamp)
  rateBuy?: number; // курс купівлі
  rateSell?: number; // курс продажу
  rateCross?: number; // крос-курс
}

class CurrencyService {
  private cache: Map<string, { rates: ExchangeRates; timestamp: number }> = new Map();
  private readonly CACHE_DURATION = 60 * 60 * 1000; // 1 година в мілісекундах
  private readonly NBU_API_URL = 'https://bank.gov.ua/NBUStatService/v1/statdirectory/exchange?json';
  private readonly MONOBANK_API_URL = 'https://api.monobank.ua/bank/currency';

  /**
   * Отримує курси валют з НБУ API
   */
  private async fetchNBURates(): Promise<NBURate[]> {
    const response = await fetch(this.NBU_API_URL);
    
    if (!response.ok) {
      throw new Error(`НБУ API error! status: ${response.status}`);
    }

    const data: NBURate[] = await response.json();
    
    if (!Array.isArray(data) || data.length === 0) {
      throw new Error('Invalid response from НБУ API');
    }

    return data;
  }

  /**
   * Конвертує курси НБУ (відносно UAH) в курси відносно USD
   * НБУ повертає rate як скільки UAH за 1 одиницю валюти
   * Наприклад: USD rate = 37 означає 1 USD = 37 UAH
   */
  private convertNBURatesToUSD(nbuRates: NBURate[]): ExchangeRates {
    const rates: ExchangeRates = {};
    
    // Знаходимо курс USD
    const usdRate = nbuRates.find(r => r.cc === 'USD');
    if (!usdRate) {
      throw new Error('USD rate not found in НБУ data');
    }

    // USD відносно USD = 1
    rates['USD'] = 1;
    
    // UAH відносно USD: якщо 1 USD = 37 UAH, то 1 UAH = 1/37 USD
    // Тобто курс UAH відносно USD = 1 / usdRate
    rates['UAH'] = 1 / usdRate.rate;

    // Конвертуємо інші валюти
    // Якщо 1 EUR = 40 UAH і 1 USD = 37 UAH, то 1 EUR = 40/37 USD
    // Тобто курс валюти відносно USD = currencyRate / usdRate
    for (const nbuRate of nbuRates) {
      if (nbuRate.cc !== 'USD' && nbuRate.rate > 0) {
        rates[nbuRate.cc] = nbuRate.rate / usdRate.rate;
      }
    }

    return rates;
  }

  /**
   * Отримує курси валют з Monobank API (резервний)
   */
  private async fetchMonobankRates(): Promise<MonobankRate[]> {
    const response = await fetch(this.MONOBANK_API_URL);
    
    if (!response.ok) {
      throw new Error(`Monobank API error! status: ${response.status}`);
    }

    const data: MonobankRate[] = await response.json();
    
    if (!Array.isArray(data)) {
      throw new Error('Invalid response from Monobank API');
    }

    return data;
  }

  /**
   * Конвертує курси Monobank в курси відносно USD
   * Monobank повертає rateBuy як скільки UAH за 1 одиницю валюти
   */
  private convertMonobankRatesToUSD(monobankRates: MonobankRate[]): ExchangeRates {
    const rates: ExchangeRates = {};
    
    // Monobank повертає курси між валютами
    // currencyCodeB = 980 означає UAH
    // Знаходимо курс USD/UAH
    const usdRate = monobankRates.find(r => r.currencyCodeA === 840 && r.currencyCodeB === 980); // 840 = USD, 980 = UAH
    
    if (!usdRate || !usdRate.rateBuy) {
      throw new Error('USD rate not found in Monobank data');
    }

    rates['USD'] = 1;
    // UAH відносно USD: якщо rateBuy = 37, то 1 UAH = 1/37 USD
    rates['UAH'] = 1 / usdRate.rateBuy;

    // Конвертуємо інші валюти
    // Знаходимо курси інших валют до UAH
    const currencyMap: Record<number, string> = {
      978: 'EUR', // EUR
      826: 'GBP', // GBP
      392: 'JPY', // JPY
      985: 'PLN', // PLN
      643: 'RUB', // RUB
    };

    for (const mbRate of monobankRates) {
      if (mbRate.currencyCodeB === 980 && mbRate.rateBuy) {
        const currencySymbol = currencyMap[mbRate.currencyCodeA];
        
        if (currencySymbol && currencySymbol !== 'USD') {
          // Курс валюти відносно USD = currencyRate / usdRate
          rates[currencySymbol] = mbRate.rateBuy / usdRate.rateBuy;
        }
      }
    }

    return rates;
  }

  /**
   * Отримує актуальні курси валют
   * @param baseCurrency базова валюта (за замовчуванням USD)
   * @returns Promise з курсами валют
   */
  async getExchangeRates(baseCurrency: string = 'USD'): Promise<ExchangeRates> {
    const cacheKey = `rates_${baseCurrency}`;
    const cached = this.cache.get(cacheKey);

    // Перевіряємо кеш
    if (cached && Date.now() - cached.timestamp < this.CACHE_DURATION) {
      return cached.rates;
    }

    try {
      // Спочатку намагаємося отримати курси з НБУ
      const nbuRates = await this.fetchNBURates();
      const rates = this.convertNBURatesToUSD(nbuRates);
      
      // Додаємо базову валюту з курсом 1, якщо її немає
      if (!rates[baseCurrency] && baseCurrency !== 'USD') {
        // Якщо базова валюта не USD, конвертуємо через USD
        if (rates['USD']) {
          rates[baseCurrency] = rates['USD'] / (rates[baseCurrency] || 1);
        }
      }

      // Зберігаємо в кеш
      this.cache.set(cacheKey, {
        rates,
        timestamp: Date.now()
      });

      return rates;
    } catch (nbuError) {
      console.warn('НБУ API failed, trying Monobank:', nbuError);
      
      try {
        // Якщо НБУ не працює, пробуємо Monobank
        const monobankRates = await this.fetchMonobankRates();
        const rates = this.convertMonobankRatesToUSD(monobankRates);
        
        // Зберігаємо в кеш
        this.cache.set(cacheKey, {
          rates,
          timestamp: Date.now()
        });

        return rates;
      } catch (monobankError) {
        console.error('Both НБУ and Monobank APIs failed:', monobankError);
        
        // Якщо обидва API не працюють, намагаємося використати кешовані дані
        if (cached) {
          console.warn('Using cached exchange rates due to API errors');
          return cached.rates;
        }

        // Якщо немає кешу, повертаємо базову валюту з курсом 1
        return { [baseCurrency]: 1 };
      }
    }
  }

  /**
   * Конвертує суму з однієї валюти в іншу
   * @param amount сума для конвертації
   * @param fromCurrency вихідна валюта
   * @param toCurrency цільова валюта
   * @returns Promise з конвертованою сумою
   */
  async convertCurrency(
    amount: number,
    fromCurrency: string,
    toCurrency: string = 'USD'
  ): Promise<number> {
    if (fromCurrency === toCurrency) {
      return amount;
    }

    // Отримуємо курси відносно toCurrency
    const rates = await this.getExchangeRates(toCurrency);
    
    // rates містить курси відносно toCurrency
    // Наприклад, rates['EUR'] = 1.08 означає що 1 EUR = 1.08 USD (якщо toCurrency = USD)
    if (rates[fromCurrency]) {
      // Конвертуємо: amount * курс_валюти_відносно_цільової
      return amount * rates[fromCurrency];
    }

    // Якщо не знайдено курс, повертаємо оригінальну суму
    console.warn(`Exchange rate not found for ${fromCurrency} to ${toCurrency}`);
    return amount;
  }

  /**
   * Конвертує масив сум в одну валюту
   * @param amounts масив об'єктів з сумою та валютою
   * @param toCurrency цільова валюта
   * @returns Promise з загальною сумою в цільовій валюті
   */
  async convertMultipleToCurrency(
    amounts: Array<{ amount: number; currency: string }>,
    toCurrency: string = 'USD'
  ): Promise<number> {
    const rates = await this.getExchangeRates(toCurrency);
    
    let total = 0;
    
    for (const { amount, currency } of amounts) {
      if (currency === toCurrency) {
        total += amount;
      } else if (rates[currency]) {
        // Конвертуємо: amount * курс_валюти_відносно_цільової
        total += amount * rates[currency];
      } else {
        console.warn(`Exchange rate not found for ${currency}, skipping`);
      }
    }

    return total;
  }

  /**
   * Очищає кеш курсів валют
   */
  clearCache(): void {
    this.cache.clear();
  }

  /**
   * Отримує список підтримуваних валют
   */
  getSupportedCurrencies(): string[] {
    return [
      'USD', 'EUR', 'GBP', 'JPY', 'AUD', 'CAD', 'CHF', 'CNY',
      'UAH', 'PLN', 'RUB', 'BGN', 'CZK', 'DKK', 'HUF', 'RON',
      'SEK', 'NOK', 'TRY', 'ILS', 'AED', 'SAR', 'KZT', 'BYN'
    ];
  }
}

export const currencyService = new CurrencyService();
